import React, {
  useMemo,
} from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import {
  resolveFixGuide,
} from "./constants/fixGuides";

const FixGuideDialog = ({
  open,
  errorType,
  onClose,
  onRetry,
  retrying = false,
}) => {
  const guide = useMemo(
    () =>
      resolveFixGuide(
        errorType || "unknown",
      ),
    [errorType],
  );

  return (
    <Dialog
      open={Boolean(open)}
      onClose={
        retrying
          ? undefined
          : onClose
      }
      fullWidth
      maxWidth="sm"
      aria-labelledby="device-fix-guide-title"
    >
      <DialogTitle
        id="device-fix-guide-title"
        sx={{
          pb: 1.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              bgcolor:
                "rgba(237,108,2,0.10)",
              color:
                "warning.main",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "center",
              flexShrink: 0,
            }}
          >
            <BuildOutlinedIcon
              fontSize="small"
            />
          </Box>

          <Box
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              component="div"
              variant="h6"
              fontWeight={700}
            >
              {guide.title}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{
                mt: 0.25,
              }}
            >
              Follow these steps and then
              retry the device check.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Stack spacing={2.5}>
          <Alert severity="warning">
            {guide.body}
          </Alert>

          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 1.25,
              }}
            >
              How to fix it
            </Typography>

            <Stack spacing={1.5}>
              {guide.steps.map(
                (step, index) => (
                  <Box
                    key={`${step.label}-${index}`}
                    sx={{
                      display: "flex",
                      alignItems:
                        "flex-start",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius:
                          "50%",
                        bgcolor:
                          "primary.main",
                        color:
                          "primary.contrastText",
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        flexShrink: 0,
                        fontSize: 13,
                        fontWeight: 700,
                        mt: 0.1,
                      }}
                    >
                      {index + 1}
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                      >
                        {step.label}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{
                          mt: 0.25,
                          lineHeight: 1.5,
                        }}
                      >
                        {step.detail}
                      </Typography>
                    </Box>
                  </Box>
                ),
              )}
            </Stack>
          </Box>

          {guide.note && (
            <Alert
              severity="info"
              icon={
                <CheckCircleOutlineOutlinedIcon />
              }
            >
              {guide.note}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="text"
          startIcon={
            <CloseOutlinedIcon />
          }
          onClick={onClose}
          disabled={retrying}
        >
          Close
        </Button>

        <Button
          variant="contained"
          startIcon={
            <RefreshOutlinedIcon />
          }
          onClick={onRetry}
          disabled={
            retrying ||
            typeof onRetry !==
              "function"
          }
        >
          {retrying
            ? "Checking..."
            : "I've Fixed It — Try Again"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixGuideDialog;